/* eslint react/no-multi-comp: 0 */
import React, { useState } from 'react';

import {
    AccordionChildItems,
    AccordionContainer,
    AccordionIcon,
    AccordionItem,
    AccordionList,
    AccordionTitle,
} from '.';

const sections = [
    { key: 'a', title: 'Section A', body: 'Content for section A.' },
    { key: 'b', title: 'Section B', body: 'Content for section B.' },
    { key: 'c', title: 'Section C', body: 'Content for section C.' },
];

// Single-open: opening one item closes the others.
export const SingleExpandAccordionExample = () => {
    const [openKey, setOpenKey] = useState<string | null>('a');

    return (
        <AccordionContainer>
            <AccordionList>
                {sections.map(section => {
                    const isActive = openKey === section.key;
                    const toggle = () =>
                        setOpenKey(isActive ? null : section.key);

                    return (
                        <AccordionItem key={section.key} isActive={isActive}>
                            <AccordionTitle onClick={toggle}>
                                {section.title}
                            </AccordionTitle>
                            <AccordionIcon size="sm" onClick={toggle} />
                            {isActive && (
                                <AccordionChildItems>
                                    {section.body}
                                </AccordionChildItems>
                            )}
                        </AccordionItem>
                    );
                })}
            </AccordionList>
        </AccordionContainer>
    );
};

// Multiple-open: each item toggles independently.
export const MultipleExpandAccordionExample = () => {
    const [openKeys, setOpenKeys] = useState<string[]>(['a']);
    const toggle = (key: string) =>
        setOpenKeys(prev =>
            prev.includes(key)
                ? prev.filter(candidate => candidate !== key)
                : [...prev, key],
        );

    return (
        <AccordionContainer>
            <AccordionList>
                {sections.map(section => {
                    const isActive = openKeys.includes(section.key);

                    return (
                        <AccordionItem key={section.key} isActive={isActive}>
                            <AccordionTitle onClick={() => toggle(section.key)}>
                                {section.title}
                            </AccordionTitle>
                            <AccordionIcon
                                size="sm"
                                onClick={() => toggle(section.key)}
                            />
                            {isActive && (
                                <AccordionChildItems>
                                    {section.body}
                                </AccordionChildItems>
                            )}
                        </AccordionItem>
                    );
                })}
            </AccordionList>
        </AccordionContainer>
    );
};

// Default-open single item.
export const DefaultOpenAccordionExample = () => {
    const [isActive, setIsActive] = useState(true);
    const toggle = () => setIsActive(prev => !prev);

    return (
        <AccordionContainer>
            <AccordionList>
                <AccordionItem isActive={isActive}>
                    <AccordionTitle onClick={toggle}>
                        Expanded by default
                    </AccordionTitle>
                    <AccordionIcon size="sm" onClick={toggle} />
                    {isActive && (
                        <AccordionChildItems>
                            This panel starts open on first render.
                        </AccordionChildItems>
                    )}
                </AccordionItem>
            </AccordionList>
        </AccordionContainer>
    );
};

// Disabled item: no toggle handler, dimmed via inline opacity.
export const DisabledAccordionExample = () => (
    <AccordionContainer>
        <AccordionList>
            <AccordionItem
                isActive={false}
                style={{ opacity: 0.5, pointerEvents: 'none' }}
                aria-disabled
            >
                <AccordionTitle>Disabled section</AccordionTitle>
                <AccordionIcon size="sm" />
            </AccordionItem>
        </AccordionList>
    </AccordionContainer>
);

// Icon emphasis: shows the caret rotating between collapsed and expanded.
export const IconAccordionExample = () => {
    const [isActive, setIsActive] = useState(false);
    const toggle = () => setIsActive(prev => !prev);

    return (
        <AccordionContainer>
            <AccordionList>
                <AccordionItem isActive={isActive}>
                    <AccordionTitle onClick={toggle}>
                        Toggle to rotate the caret
                    </AccordionTitle>
                    <AccordionIcon size="sm" onClick={toggle} />
                    {isActive && (
                        <AccordionChildItems>
                            The caret rotates when the item is active.
                        </AccordionChildItems>
                    )}
                </AccordionItem>
            </AccordionList>
        </AccordionContainer>
    );
};

// Nested child content: the child items host a nested accordion.
export const NestedChildContentAccordionExample = () => {
    const [outerOpen, setOuterOpen] = useState(true);
    const [innerOpen, setInnerOpen] = useState(false);
    const toggleOuter = () => setOuterOpen(prev => !prev);
    const toggleInner = () => setInnerOpen(prev => !prev);

    return (
        <AccordionContainer>
            <AccordionList>
                <AccordionItem isActive={outerOpen}>
                    <AccordionTitle onClick={toggleOuter}>
                        Parent section
                    </AccordionTitle>
                    <AccordionIcon size="sm" onClick={toggleOuter} />
                    {outerOpen && (
                        <AccordionChildItems>
                            <AccordionContainer>
                                <AccordionList>
                                    <AccordionItem isActive={innerOpen}>
                                        <AccordionTitle onClick={toggleInner}>
                                            Nested child
                                        </AccordionTitle>
                                        <AccordionIcon
                                            size="sm"
                                            onClick={toggleInner}
                                        />
                                        {innerOpen && (
                                            <AccordionChildItems>
                                                Nested content inside the child
                                                items.
                                            </AccordionChildItems>
                                        )}
                                    </AccordionItem>
                                </AccordionList>
                            </AccordionContainer>
                        </AccordionChildItems>
                    )}
                </AccordionItem>
            </AccordionList>
        </AccordionContainer>
    );
};
